/**
 * Utility function to handle network errors and provide a consistent way to handle
 * network-related issues including DNS resolution failures and offline states.
 */

import React from "react";

type NetworkErrorHandlerConfig = {
  onNetworkError?: (error: Error) => void;
  onOnline?: () => void;
  onOffline?: () => void;
};

export class NetworkErrorHandler {
  private static instance: NetworkErrorHandler;
  private isInitialized = false;
  private config: NetworkErrorHandlerConfig = {};

  private constructor() {
    // Private constructor to enforce singleton pattern
  }

  public static getInstance(): NetworkErrorHandler {
    if (!NetworkErrorHandler.instance) {
      NetworkErrorHandler.instance = new NetworkErrorHandler();
    }
    return NetworkErrorHandler.instance;
  }

  public initialize(config: NetworkErrorHandlerConfig = {}): void {
    if (this.isInitialized) return;

    this.config = { ...this.config, ...config };

    // Handle offline/online events
    window.addEventListener("offline", this.handleOffline);
    window.addEventListener("online", this.handleOnline);

    // Handle unhandled promise rejections (common for network errors)
    window.addEventListener(
      "unhandledrejection",
      this.handleUnhandledRejection
    );

    this.isInitialized = true;
  }

  public isNetworkError(error: unknown): boolean {
    if (!(error instanceof Error)) return false;

    const errorMessage = error.message.toLowerCase();
    const errorName = error.name.toLowerCase();

    return (
      errorMessage.includes("failed to fetch") ||
      errorMessage.includes("network request failed") ||
      errorMessage.includes("networkerror") ||
      errorMessage.includes("typeerror: failed to fetch") ||
      errorMessage.includes("err_name_not_resolved") ||
      (errorName === "typeerror" &&
        (errorMessage.includes("fetch") ||
          errorMessage.includes("network") ||
          errorMessage.includes("load failed")))
    );
  }

  private handleOffline = (): void => {
    if (this.config.onOffline) {
      this.config.onOffline();
    }
  };

  private handleOnline = (): void => {
    if (this.config.onOnline) {
      this.config.onOnline();
    }
  };

  private handleUnhandledRejection = (event: PromiseRejectionEvent): void => {
    const error = event.reason;
    if (this.isNetworkError(error) && this.config.onNetworkError) {
      this.config.onNetworkError(error);
    }
  };

  public cleanup(): void {
    if (!this.isInitialized) return;

    window.removeEventListener("offline", this.handleOffline);
    window.removeEventListener("online", this.handleOnline);
    window.removeEventListener(
      "unhandledrejection",
      this.handleUnhandledRejection
    );

    this.isInitialized = false;
  }
}

// Export a singleton instance
export const networkErrorHandler = NetworkErrorHandler.getInstance();

// Helper function to create a network error handler hook
export const useNetworkErrorHandler = (
  config: NetworkErrorHandlerConfig = {}
) => {
  React.useEffect(() => {
    networkErrorHandler.initialize(config);

    return () => {
      networkErrorHandler.cleanup();
    };
  }, [config]);
};
